<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Campaigns</h1>
        <p class="text-gray-600">Manage your email campaigns and track performance</p>
      </div>
      <button
        @click="showCreateModal = true"
        class="btn-primary"
      >
        <PlusIcon class="w-5 h-5 mr-2" />
        Create Campaign
      </button>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm p-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="form-label">Status</label>
          <select
            v-model="filters.status"
            class="input-field"
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Sending">Sending</option>
            <option value="Completed">Completed</option>
            <option value="Canceled">Canceled</option>
            <option value="Paused">Paused</option>
          </select>
        </div>
        
        <div>
          <label class="form-label">Domain</label>
          <select
            v-model="filters.domain"
            class="input-field"
          >
            <option value="">All Domains</option>
            <option
              v-for="domain in domains"
              :key="domain.DomainID"
              :value="domain.DomainID"
            >
              {{ domain.DomainName }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="form-label">Search</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Campaign name or subject..."
            class="input-field"
          />
        </div>
        
        <div class="flex items-end">
          <button
            @click="clearFilters"
            class="btn-secondary w-full"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Active Campaigns List -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">
            Active Campaigns ({{ activeCampaigns.length }})
          </h2>
          <div class="flex items-center space-x-2">
            <button
              @click="refreshCampaigns"
              :disabled="loading"
              class="btn-secondary"
            >
              <ArrowPathIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      <div class="p-6">
        <div v-if="loading" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
        
        <div v-else-if="activeCampaigns.length === 0 && finalStateCampaigns.length === 0" class="text-center py-8">
          <EnvelopeIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ campaigns.length === 0 ? 'Get started by creating your first campaign.' : 'Try adjusting your filters.' }}
          </p>
          <div v-if="campaigns.length === 0" class="mt-6">
            <button
              @click="showCreateModal = true"
              class="btn-primary"
            >
              Create Campaign
            </button>
          </div>
        </div>
        
        <div v-else-if="activeCampaigns.length === 0" class="text-center py-8 text-gray-500">
          No active campaigns
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="campaign in activeCampaigns"
            :key="campaign.CampaignID"
            class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div
                  class="w-3 h-3 rounded-full"
                  :class="getCampaignStatusColor(campaign.Status)"
                ></div>
                <div>
                  <h3 class="font-medium text-gray-900">{{ campaign.CampaignName }}</h3>
                  <p class="text-sm text-gray-500">{{ campaign.Subject }}</p>
                  <div class="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                    <span>{{ formatDateTime(campaign.ScheduledStart) }}</span>
                    <span>•</span>
                    <span>{{ campaign.TotalEmailsSent }} sent</span>
                    <span v-if="campaign.TotalEmailsBounced > 0" class="text-red-500">
                      • {{ campaign.TotalEmailsBounced }} bounced
                    </span>
                    <span>•</span>
                    <span>{{ campaign.DomainName }}</span>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center space-x-2">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getCampaignStatusBadgeClass(campaign.Status)"
                >
                  {{ campaign.Status }}
                </span>
                <div class="flex items-center space-x-2">
                  <button
                    v-if="campaign.Status === 'Sending'"
                    @click="pauseCampaign(campaign.CampaignID)"
                    class="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center"
                    title="Pause Campaign"
                  >
                    <PauseIcon class="w-4 h-4 mr-1" />
                    Pause
                  </button>
                  <button
                    v-if="campaign.Status === 'Paused'"
                    @click="resumeCampaign(campaign.CampaignID)"
                    class="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                    title="Resume Campaign"
                  >
                    <PlayIcon class="w-4 h-4 mr-1" />
                    Resume
                  </button>
                  <button
                    @click="editCampaign(campaign)"
                    class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    @click="viewCampaign(campaign)"
                    class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View Details
                  </button>
                  <button
                    @click="deleteCampaign(campaign.CampaignID)"
                    class="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Final State Campaigns List -->
    <div v-if="finalStateCampaigns.length > 0" class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">
            Completed Campaigns ({{ finalStateCampaigns.length }})
          </h2>
        </div>
      </div>
      
      <div class="p-6">
        <div class="space-y-4">
          <div
            v-for="campaign in finalStateCampaigns"
            :key="campaign.CampaignID"
            class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div
                  class="w-3 h-3 rounded-full"
                  :class="getCampaignStatusColor(campaign.Status)"
                ></div>
                <div>
                  <h3 class="font-medium text-gray-900">{{ campaign.CampaignName }}</h3>
                  <p class="text-sm text-gray-500">{{ campaign.Subject }}</p>
                  <div class="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                    <span>{{ formatDateTime(campaign.ScheduledStart) }}</span>
                    <span>•</span>
                    <span>{{ campaign.TotalEmailsSent }} sent</span>
                    <span v-if="campaign.TotalEmailsBounced > 0" class="text-red-500">
                      • {{ campaign.TotalEmailsBounced }} bounced
                    </span>
                    <span>•</span>
                    <span>{{ campaign.DomainName }}</span>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center space-x-2">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getCampaignStatusBadgeClass(campaign.Status)"
                >
                  {{ campaign.Status }}
                </span>
                <div class="flex items-center space-x-2">
                  <button
                    @click="viewCampaign(campaign)"
                    class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View Details
                  </button>
                  <button
                    @click="deleteCampaign(campaign.CampaignID)"
                    class="text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Campaign Modal -->
    <CampaignModal
      v-if="showCreateModal"
      :campaign="editingCampaign"
      :domains="domains"
      :emailLists="emailLists"
      @close="closeModal"
      @saved="onCampaignSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { campaignsApi, domainsApi, listsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import {
  PlusIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  PauseIcon,
  PlayIcon
} from '@heroicons/vue/24/outline'
import CampaignModal from '../components/CampaignModal.vue'

const router = useRouter()

// State
const campaigns = ref([])
const domains = ref([])
const emailLists = ref([])
const loading = ref(false)
const showCreateModal = ref(false)
const editingCampaign = ref(null)

// Filters
const filters = ref({
  status: '',
  domain: '',
  search: ''
})

// Computed
const filteredCampaigns = computed(() => {
  let filtered = campaigns.value

  if (filters.value.status) {
    filtered = filtered.filter(c => c.Status === filters.value.status)
  }

  if (filters.value.domain) {
    filtered = filtered.filter(c => c.DomainID == filters.value.domain)
  }

  if (filters.value.search) {
    const search = filters.value.search.toLowerCase()
    filtered = filtered.filter(c => 
      c.CampaignName.toLowerCase().includes(search) ||
      c.Subject.toLowerCase().includes(search)
    )
  }

  return filtered
})

// Separate campaigns into active and final state
const activeCampaigns = computed(() => {
  return filteredCampaigns.value.filter(c => 
    c.Status !== 'Completed' && c.Status !== 'Canceled'
    // Includes: Draft, Scheduled, Sending, Paused
  )
})

const finalStateCampaigns = computed(() => {
  return filteredCampaigns.value.filter(c => 
    c.Status === 'Completed' || c.Status === 'Canceled'
  )
})

// Methods
const getCampaignStatusColor = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-500'
    case 'Sending':
      return 'bg-success-500'
    case 'Completed':
      return 'bg-gray-400'
    case 'Canceled':
      return 'bg-red-400'
    case 'Paused':
      return 'bg-gray-300'
    default:
      return 'bg-gray-400'
  }
}

const getCampaignStatusBadgeClass = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-100 text-warning-800'
    case 'Sending':
      return 'bg-success-100 text-success-800'
    case 'Completed':
      return 'bg-gray-100 text-gray-800'
    case 'Canceled':
      return 'bg-red-100 text-red-800'
    case 'Paused':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'Not scheduled'
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  if (!dateString) return 'Not scheduled'
  return new Date(dateString).toLocaleString()
}

const fetchCampaigns = async () => {
  try {
    loading.value = true
    const response = await campaignsApi.getAll()
    campaigns.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching campaigns:', error)
    toast.error('Failed to fetch campaigns')
  } finally {
    loading.value = false
  }
}

const fetchDomains = async () => {
  try {
    const response = await domainsApi.getAll()
    domains.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching domains:', error)
  }
}

const fetchEmailLists = async () => {
  try {
    const response = await listsApi.getAll()
    emailLists.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching email lists:', error)
  }
}

const refreshCampaigns = () => {
  fetchCampaigns()
}

const clearFilters = () => {
  filters.value = {
    status: '',
    domain: '',
    search: ''
  }
}

const editCampaign = (campaign) => {
  editingCampaign.value = campaign
  showCreateModal.value = true
}

const viewCampaign = (campaign) => {
  router.push(`/campaigns/${campaign.CampaignID}`)
}

const pauseCampaign = async (campaignId) => {
  try {
    await campaignsApi.pause(campaignId)
    toast.success('Campaign paused successfully')
    await fetchCampaigns()
  } catch (error) {
    console.error('Error pausing campaign:', error)
    toast.error(error.response?.data?.error || 'Failed to pause campaign')
  }
}

const resumeCampaign = async (campaignId) => {
  try {
    await campaignsApi.resume(campaignId)
    toast.success('Campaign resumed successfully')
    await fetchCampaigns()
  } catch (error) {
    console.error('Error resuming campaign:', error)
    toast.error(error.response?.data?.error || 'Failed to resume campaign')
  }
}

const deleteCampaign = async (campaignId) => {
  if (!confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
    return
  }

  try {
    await campaignsApi.delete(campaignId)
    toast.success('Campaign deleted successfully')
    await fetchCampaigns()
  } catch (error) {
    console.error('Error deleting campaign:', error)
    toast.error('Failed to delete campaign')
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingCampaign.value = null
}

const onCampaignSaved = async () => {
  closeModal()
  await fetchCampaigns()
  toast.success('Campaign saved successfully')
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCampaigns(),
    fetchDomains(),
    fetchEmailLists()
  ])
})
</script> 